import { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useHaptics } from './useHaptics';

/** The league a profile should default its picks to, captured from where you tapped. */
export interface ProfileContext {
  leagueId?: string | null;
  roundId?: string | null;
}

/**
 * Navigate to a player's profile from anywhere a user is shown.
 *
 * Every surface that renders another player — standings rows, the division board,
 * mini-league tables, duel heroes, member lists — should let you tap through to
 * their profile, so the social graph is always one tap away. Your own row routes
 * to your league-scoped profile (`/profile`, which redirects into the active
 * league); everyone else opens the public `/u/:userId` profile.
 *
 * The profile shows the player's picks for a league you share. To avoid surfacing
 * the wrong league (e.g. tapping a rival on your World Cup board but seeing their
 * cycling picks), we carry a league hint: an explicit `context` wins, otherwise we
 * infer it from the current `/play/:leagueId` route — so a tap on a league's board
 * always opens that league's picks. The bare `/u/:userId` share link stays clean.
 */
export function useOpenProfile() {
  const navigate = useNavigate();
  const location = useLocation();
  const { impact } = useHaptics();
  return useCallback(
    (userId: string | null | undefined, isYou = false, context?: ProfileContext) => {
      impact('light');
      if (isYou) {
        const inferredLeague = context?.leagueId ?? location.pathname.match(/^\/play\/([^/]+)/)?.[1] ?? null;
        navigate(inferredLeague ? `/play/${inferredLeague}/profile` : '/profile');
        return;
      }
      if (!userId) return;
      const inferredLeague = location.pathname.match(/^\/play\/([^/]+)/)?.[1] ?? null;
      const leagueId = context?.leagueId ?? inferredLeague;
      const roundId = context?.roundId ?? null;
      const params = new URLSearchParams();
      if (leagueId) params.set('league', leagueId);
      if (roundId) params.set('round', roundId);
      const qs = params.toString();
      navigate(`/u/${userId}${qs ? `?${qs}` : ''}`);
    },
    [navigate, location.pathname, impact]
  );
}
